import type { ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject, ViewChild } from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import type { MegaErrors } from '../../../mega-exchange/services/interfaces/mega-error';
import { FmLoadingPageService } from '../../../shared/services/fm-loading-page.service';
import { UserService } from '../../../shared/services/user.service';
import { CancelExchangeService } from '../../services/cancel-exchange.service';
import type { CancelExchangeResponse } from '../../services/interfaces/cancel-exchange-response';
import type { CommitExchangeResponse } from '../../services/interfaces/commit-exchange-response';
import type {
  Dealer,
  ListOfDealerTraderResponse,
  Trader,
} from '../../services/interfaces/list-dealer-trader-response';
import type { RecombinationRequest } from '../../services/interfaces/ListRecombinationsRequest';
import type {
  AtSettlementDeal,
  RCRTakedownResponse,
} from '../../services/interfaces/RCRTakedownResponse';
import type { TakedownReloadResponse } from '../../services/interfaces/takedown-reload-response';
import { PassdataService } from '../../services/passdata.service';
import { RcrExchangeNavigationServiceService } from '../../services/rcr-exchange-navigation-service.service';
import { RecombinationsService } from '../../services/recombinations.service';
import { ToggleFeature } from '../../../shared/entitlement/toggle-feature';
import { FeatureFlag } from '../../../common/enums/feature-flag.enum';
import { formatUnixTimestampToEst, isPastEasternTimeCutoff } from '../../../common/util/date.util';
import { StringConstants } from '../../../constants/string-constants';

// External/dealer takedowns become read-only starting at 12:00 PM Eastern Time on the
// calendar day before the deal's settlement date. See story MRKSCM061-1076.
const CUTOFF_DAYS_BEFORE_SETTLEMENT = 1;
const CUTOFF_HOUR_ET = 12;

@Component({
  standalone: false,
  selector: 'invd-available-rcr-takedown-listings',
  templateUrl: './available-rcr-takedown-listings.component.html',
  styleUrls: ['./available-rcr-takedown-listings.component.scss'],
})
export class AvailableRcrTakedownListingsComponent implements OnInit, OnDestroy {
  public rcrTakedownData: any = new Object();
  public isInternalUsr = true;
  public showCutoffBanner = false;
  protected readonly cutoffBannerMessage = StringConstants.RCR_TAKEDOWN_CUTOFF_BANNER_MESSAGE;
  public presentSortedColumn = '';
  public presentSortedColumnIsAscending = false;
  public rcrTakedownResponseError = false;
  public rcrTakedownResponseErrorObject: MegaErrors | undefined;
  public cancelTakeDownModelWindow: any = {
    show: false,
    commitExchange: {},
    showCancelconfirmation: false,
    CancelconfirmationMessage: '',
  };
  public reloadTakeDownModelWindow: any = {
    show: false,
    commitExchange: {},
    showReloadconfirmation: false,
    ReloadconfirmationMessage: '',
  };
  public errorStr = '';
  public dealers: Dealer[] = [];
  public selectedDealer: Dealer | null = null;
  public selectedAssociatedTrader: Trader | null = null;
  public showDealerTraderModel = false;
  public takedownModalMessage = '';
  public takeDownIndex = -1;
  public takedownErrorMessage: any;
  public takedownHasError: any;
  public requestExchangeForm!: UntypedFormGroup;
  public isViewLinkClicked = true;
  public showReloadTakedownSuccessMsg = false;
  public noTakedownCheckbox: any;
  public showTakedownModal = false;
  private currentDeal: any;

  @ViewChild('exchangeCancelledOnButtonClick') exchangeCancelledOnButtonClick!: ElementRef;
  @ViewChild('remicReloadOnButtonClick') remicReloadOnButtonClick!: ElementRef;

  private userService = inject(UserService);
  private router = inject(Router);
  private rcrService = inject(RecombinationsService);
  private rcrNavigationService = inject(RcrExchangeNavigationServiceService);
  private passData = inject(PassdataService);
  private fmLoadingPageService = inject(FmLoadingPageService);
  private cancelExchangeService = inject(CancelExchangeService);
  private fb = inject(UntypedFormBuilder);
  private cdr = inject(ChangeDetectorRef);

  public enableEdit = ToggleFeature.isToggleFeatureEnabled(
    this.userService.toggleFeatures,
    FeatureFlag.TAKEDOWN_EDIT_FLAG,
  );

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.isInternalUsr = this.userService.isInternalUser;
    this.isViewLinkClicked = true;
    this.getRcrTakedownData();

    this.getListDealerTraders();
    this.requestExchangeForm = this.fb.group({
      dealerControl: new UntypedFormControl(),
      traderControl: new UntypedFormControl(),
    });
  }

  ngOnDestroy() {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
  }

  private showTakedownErrorMessage(error: boolean, errorObject: any): void {
    if (error) {
      this.takedownErrorMessage = new Object();
      this.takedownHasError = error;
      this.takedownErrorMessage.title = 'Status';
      this.takedownErrorMessage.description = errorObject.error.messages[0].errorMessage;
    } else {
      this.takedownErrorMessage = null;
    }
  }

  protected submitTraderDetails(): void {
    this.showDealerTraderModel = false;
    this.fmLoadingPageService.isLoaderRequesting.set(true);

    const idx = this.takeDownIndex;
    const recombinationRequestData: any = new Object();
    recombinationRequestData.sessionToken = this.rcrTakedownData.sessionToken;
    recombinationRequestData.sttsTraderId = this.userService.getTraderId();
    recombinationRequestData.userSearchInput =
      this.rcrTakedownData.atSettlementDeals[idx].dealNumber;
    recombinationRequestData.dealSubType = this.rcrTakedownData.atSettlementDeals[idx].dealSubType;
    recombinationRequestData.takedownFlow = true;
    recombinationRequestData.exchangeConfirmationNumber =
      this.rcrTakedownData.atSettlementDeals[idx].exchangeConfirmationNumber;

    this.rcrService.setRecombinationRequestData(recombinationRequestData as RecombinationRequest);
    this.passData.setExchangeConfirmationNumber(
      recombinationRequestData.exchangeConfirmationNumber,
    );
    this.passData.setRecombinationDataCache(null);
    this.rcrNavigationService.canNavigate = true;

    this.router
      .navigate([
        'rcr-exchange/rcr-takedown',
        recombinationRequestData.userSearchInput,
        'enter-takedowns',
      ])
      .then((navigated) => {
        this.fmLoadingPageService.isLoaderRequesting.set(false);
        if (!navigated) {
          const recombinationResponse: any = this.rcrService.getRecombinationResponseData();
          this.showTakedownErrorMessage(true, recombinationResponse);
          window.scrollTo(0, 0);
        }
      });
  }

  public onDealerSelect(dealer: Dealer | null): void {
    this.selectedDealer = dealer;
    this.selectedAssociatedTrader = null;
    if (dealer != null) {
      this.requestExchangeForm.controls.traderControl.reset();
      this.userService.setDealerName(dealer.dealerName);
    }
  }

  protected onTraderSelect(trader: Trader): void {
    this.selectedAssociatedTrader = trader;
    if (trader != null) {
      this.userService.setTrader(
        trader.traderFirstName + ' ' + trader.traderLastName,
        trader.traderId,
      );
    }
  }

  protected takeDownClick(idx: number): void {
    if (this.isTakedownReadOnly(this.rcrTakedownData.atSettlementDeals[idx])) {
      return;
    }
    if (this.isInternalUsr) {
      this.takeDownIndex = idx;
      this.onDealerSelect(this.getDealerByID(this.rcrTakedownData.atSettlementDeals[idx].dealerId));
      this.showDealerTraderModel = true;
    } else {
      // set deal/treader for external user
      this.takeDownIndex = idx;
      this.selectedDealer = this.dealers[0];
      this.selectedAssociatedTrader = this.dealers[0].traders[0];
      this.userService.setDealerName(this.selectedDealer.dealerName);
      this.userService.setTrader(
        this.selectedAssociatedTrader.traderFirstName +
          ' ' +
          this.selectedAssociatedTrader.traderLastName,
        this.selectedAssociatedTrader.traderId,
      );
      this.submitTraderDetails();
    }
  }

  protected reloadRecomClicked(_takedown: any): void {
    if (this.isTakedownReadOnly(_takedown)) {
      return;
    }
    this.reloadTakeDownModelWindow.show = true;
    this.reloadTakeDownModelWindow.atSettlementDeals = _takedown;
  }

  public reloadRecomService(_takedown: any): void {
    this.remicReloadOnButtonClick.nativeElement.disabled = true;
    this.reloadTakeDownModelWindow.show = false;
    const reloadTakedownRequestData: any = new Object();

    reloadTakedownRequestData.dealNumber =
      this.reloadTakeDownModelWindow.atSettlementDeals.dealNumber;
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.takedownReloadServiceCall(reloadTakedownRequestData);
  }
  private takedownReloadServiceCall(request: any): void {
    this.rcrService.takedownReload(request).subscribe({
      next: (data) => this.handleReloadtakedownResponse(data as TakedownReloadResponse),
      error: (error) => this.handleReloadtakedownResponseError(error),
    });
  }

  private handleReloadtakedownResponse(takedownReloadResponse: TakedownReloadResponse): void {
    this.showReloadTakedownSuccessMsg =
      takedownReloadResponse &&
      takedownReloadResponse.displayMessage &&
      takedownReloadResponse.displayMessage.includes('Success!')
        ? true
        : false;

    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.reloadTakeDownModelWindow.show = false;
    this.reloadTakeDownModelWindow.showReloadconfirmation = true;
    this.reloadTakeDownModelWindow.ReloadConfirmationMessage =
      takedownReloadResponse.displayMessage;
    this.getRcrTakedownData();
    this.cdr.markForCheck();
  }
  private handleReloadtakedownResponseError(error: any) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.reloadTakeDownModelWindow.show = false;
    this.reloadTakeDownModelWindow.showReloadconfirmation = true;
    this.reloadTakeDownModelWindow.ReloadConfirmationMessage = error.displayMessage;
    this.getRcrTakedownData();
    this.cdr.markForCheck();
  }

  private getDealerByID(dealerID: number): Dealer | null {
    return this.dealers.find((dealer) => dealer.dealerID === dealerID) ?? null;
  }

  protected retrieveExchangeData(availableRcrTakedown: AtSettlementDeal): void {
    //exchangeConfirmationNumber
    const retrieveExchangeRequestData: any = new Object();
    //#TODO remove code when backend updates webservice
    //retrieveExchangeRequestData['sttsTraderId'] = this.userService.getTraderId();
    const exchangeConfirmationNumber: number = availableRcrTakedown.exchangeConfirmationNumber;
    retrieveExchangeRequestData.exchangeConfirmationNumber = exchangeConfirmationNumber;
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    if (this.isViewLinkClicked) {
      this.retrieveExchangeServiceCall(retrieveExchangeRequestData);
    }

    this.isViewLinkClicked = false;
  }

  private retrieveExchangeServiceCall(request: any): void {
    this.rcrService.retrieveExchangeService(request).subscribe({
      next: (data) => this.handleRetrieveExchangeResponse(data as CommitExchangeResponse),
      error: (error) => this.handleRetrieveExchangeResponseError(error),
    });
  }

  private handleRetrieveExchangeResponse(commitExchangeResp: CommitExchangeResponse): void {
    this.isViewLinkClicked = true;
    this.passData.setCommitExchangeResponse(commitExchangeResp);
    this.fmLoadingPageService.stopLoading();
    this.router.navigate([
      'rcr-exchange/settled-exchanges',
      commitExchangeResp.exchangeConfirmationNumber,
      'confirm',
    ]);
  }

  private handleRetrieveExchangeResponseError(_error: any): void {
    this.isViewLinkClicked = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  getRcrTakedownData(): void {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.rcrService.getRcrTakedown().subscribe({
      next: (data) => this.handleRcrTakedownResponse(data as RCRTakedownResponse),
      error: (error) => this.handleRcrTakedownResponseError(error),
    });
  }

  private handleRcrTakedownResponse(rcrTakedownObject: RCRTakedownResponse): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.rcrTakedownData = rcrTakedownObject;
    this.rcrTakedownResponseError = false;
    this.showCutoffBanner = (rcrTakedownObject?.atSettlementDeals || []).some(
      (deal: AtSettlementDeal) => this.isTakedownReadOnly(deal),
    );
    this.cdr.markForCheck();
  }

  /**
   * Determines whether a takedown row is read-only for the current user. Internal users are
   * power users and are never subject to the cutoff restriction; external/dealer users become
   * read-only at 12:00 PM Eastern Time on the calendar day before the deal's settlement date.
   */
  public isTakedownReadOnly(takedown: AtSettlementDeal): boolean {
    if (this.isInternalUsr || !takedown?.dealSettlementDate) {
      return false;
    }
    return isPastEasternTimeCutoff(
      takedown.dealSettlementDate,
      CUTOFF_DAYS_BEFORE_SETTLEMENT,
      CUTOFF_HOUR_ET,
    );
  }

  protected submitWithoutTakedown(): void {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    const payload = {
      dealNumber: this.currentDeal.dealNumber,
      noTakedownFlag: this.currentDeal.noTakedownFlag,
      isUserUdpate: true,
    };
    this.rcrService.sendNoTakedownEmail(payload).subscribe({
      next: () => this.handleSendNoTakedownEmailResponse(),
      error: () => this.handleSendNoTakedownEmailResponseError(),
    });
  }

  private handleSendNoTakedownEmailResponse(): void {
    const deal = this.rcrTakedownData.atSettlementDeals.find(
      (d: any) => d.dealNumber === this.cancelTakeDownModelWindow.atSettlementDeals?.dealNumber,
    );
    this.currentDeal = deal;
    this.currentDeal.noTakedownFlag = false;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cancelTakeDownModelWindow = false;
    this.cdr.markForCheck();
  }

  private handleSendNoTakedownEmailResponseError(): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cancelTakeDownModelWindow = false;
    this.cdr.markForCheck();
  }

  protected sortByGridByColumnName(columnName: string): void {
    if (this.rcrTakedownData.hasOwnProperty('atSettlementDeals')) {
      if (this.presentSortedColumn !== columnName) {
        this.presentSortedColumnIsAscending = true;
      } else {
        this.presentSortedColumnIsAscending = !this.presentSortedColumnIsAscending;
      }
      const isAscending = this.presentSortedColumnIsAscending ? 1 : -1;

      this.rcrTakedownData.atSettlementDeals.sort((a: any, b: any) => {
        const textA = a[columnName].toUpperCase();
        const textB = b[columnName].toUpperCase();
        return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
      });
      this.presentSortedColumn = columnName;
    }
  }

  // #TODO RCR Takedown Error Scenarios
  private handleRcrTakedownResponseError(error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.rcrTakedownResponseError = true;
    this.rcrTakedownResponseErrorObject = error && error.error;
    this.errorStr = this.rcrTakedownResponseErrorObject?.messages[0].errorMessage ?? '';
    this.cdr.markForCheck();
  }

  protected cancelTakeDownClick(_takedown: any): void {
    if (this.isTakedownReadOnly(_takedown)) {
      return;
    }
    this.cancelTakeDownModelWindow.show = true;
    this.cancelTakeDownModelWindow.atSettlementDeals = _takedown;
  }

  protected callCancelExchangeService(_takedown: any): void {
    this.exchangeCancelledOnButtonClick.nativeElement.disabled = true;
    const cancelTakeDownRequestData: any = new Object();
    cancelTakeDownRequestData.exchangeConfirmationNumber =
      this.cancelTakeDownModelWindow.atSettlementDeals.exchangeConfirmationNumber;
    if (this.cancelTakeDownModelWindow.atSettlementDeals.noTakedownFlag) {
      const payload = {
        dealNumber: this.cancelTakeDownModelWindow.atSettlementDeals.dealNumber,
        noTakedownFlag: false,
        isUserUdpate: true,
      };
      this.rcrService.sendNoTakedownEmail(payload).subscribe({
        next: () => this.handleSendNoTakedownEmailResponse(),
        error: () => this.handleSendNoTakedownEmailResponseError(),
      });
    } else {
      this.cancelTakeDownServiceCall(cancelTakeDownRequestData);
    }
  }

  private cancelTakeDownServiceCall(request: any): void {
    this.cancelExchangeService.cancelExchangeService(request).subscribe({
      next: (data) => this.handleCancelExchangeResponse(data as CancelExchangeResponse),
      error: (error) => this.handleRetrieveExchangeResponseError(error),
    });
  }

  private handleCancelExchangeResponse(cancelExchangeResp: CancelExchangeResponse): void {
    this.cancelTakeDownModelWindow.show = false;
    this.cancelTakeDownModelWindow.showCancelConfirmation = true;
    this.cancelTakeDownModelWindow.CancelConfirmationMessage = cancelExchangeResp.displayMessage;
    this.getRcrTakedownData();
    this.cdr.markForCheck();
  }
  private handleCancelExchangeResponseError(_error: any) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  private getListDealerTraders(): void {
    if (this.passData.getCustomers() != null) {
      this.dealers = this.passData.getCustomers();
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      return;
    }
    this.rcrService.getListDealersTradersService().subscribe({
      next: (data) => this.handelListDealerTraderServiceSuccess(data as ListOfDealerTraderResponse),
      error: (error) => this.handleListDealerTraderServiceFail(error),
    });
  }

  private handelListDealerTraderServiceSuccess(listDealerTrader: ListOfDealerTraderResponse) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.dealers = listDealerTrader.dealer;
    this.passData.setCustomers(this.dealers);
    this.cdr.markForCheck();
  }

  private handleListDealerTraderServiceFail(_error: any) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  formatDate(timestamp: number): string {
    return formatUnixTimestampToEst(timestamp);
  }

  protected onCheckboxSelect(event: any, dealNumber: any): void {
    const deal = this.rcrTakedownData.atSettlementDeals.find(
      (d: any) => d.dealNumber === dealNumber,
    );
    if (this.isTakedownReadOnly(deal)) {
      return;
    }
    if (event.target.checked) {
      this.takedownModalMessage = StringConstants.TAKEDOWN_MODAL_MESSAGE;
    } else {
      this.takedownModalMessage = StringConstants.TAKEDOWN_CANCEL_MODAL_MESSAGE;
    }
    this.showTakedownModal = true;
    this.currentDeal = deal;
    this.currentDeal.noTakedownFlag = !deal.noTakedownFlag;
  }

  protected cancelTakedownModal() {
    this.showTakedownModal = false;
    const deal = this.rcrTakedownData.atSettlementDeals.find(
      (d: any) => d.dealNumber === this.currentDeal.dealNumber,
    );
    this.currentDeal = deal;
    this.currentDeal.noTakedownFlag = !deal.noTakedownFlag;
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }

  private isMfOrSf(dealSubType: string): boolean {
    return (
      dealSubType === StringConstants.EXCHANGE_TYPE_MF ||
      dealSubType === StringConstants.EXCHANGE_TYPE_SF
    );
  }

  public getTakedownSubStatus(takedown: AtSettlementDeal): string {
    if (takedown?.takeDownStatus === StringConstants.TAKEDOWN_STATUS_NEW) {
      if (!takedown.noTakedownFlag && this.isMfOrSf(takedown.dealSubType)) {
        if (takedown.remicRecombinationCount === 0) {
          return StringConstants.SUBSTATUS_NO_SUBMISSION_REQUIRED;
        }
        if (takedown.remicRecombinationCount > 0) {
          return StringConstants.SUBSTATUS_SUBMISSION_REQUIRED;
        }
      }
      return takedown.noTakedownFlag
        ? StringConstants.SUBSTATUS_SUBMITTED_NO_TAKEDOWN
        : StringConstants.SUBSTATUS_SUBMISSION_REQUIRED;
    }
    if (takedown?.takeDownStatus === StringConstants.TAKEDOWN_STATUS_SUBMITTED) {
      return StringConstants.TAKEDOWN_STATUS_SUBMITTED;
    }
    return takedown?.takeDownStatus || '';
  }

  public getTakedownSubStatusClass(takedown: AtSettlementDeal): string {
    const subStatus = this.getTakedownSubStatus(takedown);

    if (
      subStatus === StringConstants.SUBSTATUS_NO_SUBMISSION_REQUIRED ||
      subStatus === StringConstants.SUBSTATUS_SUBMITTED_NO_TAKEDOWN ||
      subStatus === StringConstants.TAKEDOWN_STATUS_SUBMITTED
    ) {
      return 'status-green';
    }
    if (subStatus === StringConstants.SUBSTATUS_SUBMISSION_REQUIRED) {
      return 'status-red';
    }
    return '';
  }
}
